import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';

import { DprPersonnelDetailGridData } from '@dpr-details/models/grid';
import { PersonnelVersion } from '@shared/enums';
import { ProjectSetup } from '@shared/models';
import { DprPersonnelDetail, WorkedHoursDetails } from '@shared/models/dpr';
import { GridSync } from '@grid/models';
import { DailyStockService } from '../daily-stock.service';

@Injectable()
export class DprPersonnelGridService {
  constructor(private dailyStockService: DailyStockService) {}

  buildGridData(
    projectSetup: ProjectSetup,
    dprPersonnelDetails: DprPersonnelDetail[]
  ): DprPersonnelDetailGridData[] {
    const data = [];
    const personnelVersion =
      projectSetup.subprojectConfiguration.personnelVersion;

    projectSetup.personnelList.forEach((item) => {
      const dprPersonnelDetail = dprPersonnelDetails.find(
        (d) => d.personnelId === item.id
      );

      const entity: DprPersonnelDetailGridData = {
        id: uuid(),
        dprPersonnelId: null,
        priority: item.priority,
        name: item.name,
        previous: 0,
        today: null,
        totalEndOfDayDisplay: null,
        totalUsedDisplay: null,
        total: 0,
        currentDay: null,
        workedHours: null,
        comment: null,
        personnelId: item.id,
      };

      if (dprPersonnelDetail) {
        entity.id = dprPersonnelDetail.id ?? entity.id;
        entity.dprPersonnelId = dprPersonnelDetail.dprPersonnelId;
        entity.previous = dprPersonnelDetail.previous;
        entity.today = dprPersonnelDetail.today;
        entity.added = dprPersonnelDetail.added;
        entity.used = dprPersonnelDetail.used;

        // needed for calculations
        entity.dailyStockDetails = { ...dprPersonnelDetail.dailyStockDetails };

        // flatten the dailyStockDetails to be able to use it in the grid
        entity.dailyStockDetailsTotalStartOfDay =
          dprPersonnelDetail.dailyStockDetails?.totalStartOfDay;
        entity.dailyStockDetailsTotalEndOfDay =
          dprPersonnelDetail.dailyStockDetails?.totalEndOfDay;
        entity.dailyStockDetailsTotalUsed =
          dprPersonnelDetail.dailyStockDetails?.totalUsed;

        entity.total = this.calculateTotal(
          dprPersonnelDetail.previous,
          dprPersonnelDetail.today
        );
        entity.currentDay = dprPersonnelDetail.currentDay;
        entity.workedHours =
          personnelVersion !== PersonnelVersion.Basic
            ? dprPersonnelDetail?.workedHours ?? null
            : null;

        // needed for calculations
        entity.workedHoursDetails = {
          ...dprPersonnelDetail.workedHoursDetails,
        };

        // flatten the workedHoursDetails to be able to use it in the grid
        entity.workedHoursDetailsTotal =
          dprPersonnelDetail.workedHoursDetails?.total;
        entity.workedHoursDetailsTotalToDate =
          dprPersonnelDetail.workedHoursDetails?.totalToDate;
        entity.workedHoursDetailsPreviousTotalToDate =
          dprPersonnelDetail.workedHoursDetails?.previousTotalToDate;

        entity.comment = dprPersonnelDetail.comment;
      }

      data.push(entity);
    });

    return data;
  }

  mapToGridSync(
    dprPersonnelId: number | string,
    changes: GridSync<DprPersonnelDetailGridData>
  ): GridSync<DprPersonnelDetail> {
    const result = {
      addedList: [],
      updatedList: [],
      deletedIds: changes?.deletedIds ?? [],
    };

    const mappingFunction = (data: DprPersonnelDetailGridData) => ({
      id: data.id,
      dprPersonnelId,
      personnelId: data.personnelId,
      previous: data.previous,
      today: data.today,
      workedHours: data.workedHours,
      added: data.added,
      used: data.used,
      currentDay: data.currentDay,
      comment: data.comment,
      dailyStockDetails: this.dailyStockService.buildDailyStockDetails(data),
      workedHoursDetails: this.buildWorkedHoursDetails(data),
    });

    result.addedList.push(...(changes?.addedList?.map(mappingFunction) ?? [])); // needed for offline
    result.addedList.push(
      ...(changes?.updatedList
        ?.filter((x) => isNaN(+x.id))
        .map(mappingFunction) ?? [])
    );

    result.updatedList = changes?.updatedList
      ?.filter((x) => !isNaN(+x.id))
      .map(mappingFunction);

    return result;
  }

  shouldTriggerAutoFill(
    data: DprPersonnelDetailGridData[],
    projectSetup: ProjectSetup
  ): boolean {
    const personnelVersion =
      projectSetup.subprojectConfiguration.personnelVersion;

    let someItemsFilled: boolean;
    let someItemsEmpty: boolean;

    switch (personnelVersion) {
      case PersonnelVersion.Basic:
        someItemsFilled = data.some((reg) => reg.today != null);
        someItemsEmpty = data.some((reg) => reg.today == null);
        break;
      case PersonnelVersion.Stock:
        return this.dailyStockService.shouldTriggerAutoFillOfStock(data);
      case PersonnelVersion.Classic:
        someItemsFilled = data.some((reg) => reg.currentDay != null);
        someItemsEmpty = data.some((reg) => reg.currentDay == null);
        break;
    }

    return someItemsFilled && someItemsEmpty;
  }

  calculateTotal(previous: number, today: number): number {
    return previous + (today ?? 0);
  }

  //#region Private methods

  private buildWorkedHoursDetails(
    data: DprPersonnelDetailGridData
  ): WorkedHoursDetails {
    if (!data) {
      return null;
    }

    return {
      total: data.workedHoursDetailsTotal,
      totalToDate: data.workedHoursDetailsTotalToDate,
      previousTotalToDate: data.workedHoursDetailsPreviousTotalToDate,
    };
  }

  //#endregion
}
